/**
 * Shared split-field helpers for preview overlays and canvas PDF export.
 * Split fields store per-box relative coords in fieldCoordinates.splitParts.
 * Legacy PdfFieldPlacer fields may only have fieldCoordinates.segmentation.
 */

export type SplitPart = {
  x: number;
  y: number;
  width: number;
  height: number;
  start: number;
  length: number;
};

type SegmentationLike = {
  enabled?: boolean;
  mode?: "auto" | "fixed";
  count?: number;
  boundaries?: number[];
};

export function clamp01(v: number): number {
  if (Number.isNaN(v)) return 0;
  return Math.min(1, Math.max(0, v));
}

function clampInt(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, Math.round(value)));
}

export function cleanForSlice(value: string): string {
  return (value || "").replace(/[^a-zA-Z0-9]/g, "");
}

export function sliceValue(value: string, start: number, length: number): string {
  const s = Math.max(0, Math.floor(Number(start)) || 0);
  const len = Math.max(0, Math.floor(Number(length)) || 0);
  return cleanForSlice(value).substring(s, s + len);
}

function normalizeSplitPart(raw: Partial<SplitPart> & Record<string, unknown>): SplitPart | null {
  const x = clamp01(Number(raw.x));
  const y = clamp01(Number(raw.y));
  const width = clamp01(Number(raw.width));
  const height = clamp01(Number(raw.height));
  if (width <= 0 || height <= 0) return null;

  const start = Math.max(0, Math.floor(Number(raw.start ?? raw.sliceStart)) || 0);
  const length = Math.max(
    0,
    Math.floor(Number(raw.length ?? raw.len ?? raw.sliceLength)) || 0
  );
  if (length <= 0) return null;

  return { x, y, width, height, start, length };
}

export function getSplitParts(coords: unknown): SplitPart[] | null {
  const raw = (coords as { splitParts?: unknown[] } | null)?.splitParts;
  if (!Array.isArray(raw) || raw.length === 0) return null;

  const parts = raw
    .map((p) =>
      normalizeSplitPart(p as Partial<SplitPart> & Record<string, unknown>)
    )
    .filter((p): p is SplitPart => p !== null);

  return parts.length > 0 ? parts : null;
}

function baseRelativeBox(
  coords: unknown
): { x: number; y: number; width: number; height: number } | null {
  const c = coords as Record<string, unknown> | null;
  if (!c || c.unit !== "relative") return null;
  if (
    typeof c.x !== "number" ||
    typeof c.y !== "number" ||
    typeof c.width !== "number" ||
    typeof c.height !== "number"
  ) {
    return null;
  }
  return {
    x: clamp01(c.x),
    y: clamp01(c.y),
    width: clamp01(c.width),
    height: clamp01(c.height),
  };
}

function readSegmentation(coords: unknown): SegmentationLike | null {
  const c = coords as Record<string, unknown> | null;
  if (!c) return null;
  const raw = (c.segmentation ?? c.segments) as SegmentationLike | undefined;
  if (!raw || typeof raw !== "object") return null;
  if (raw.enabled === false) return null;
  return raw;
}

function segmentCountFromSegmentation(
  segmentation: SegmentationLike,
  box: { width: number; height: number }
): number {
  if (Array.isArray(segmentation.boundaries) && segmentation.boundaries.length) {
    return clampInt(segmentation.boundaries.length + 1, 1, 60);
  }
  if (segmentation.mode === "fixed" && segmentation.count) {
    return clampInt(segmentation.count, 1, 60);
  }
  const h = Math.max(0.001, box.height);
  const estimated = Math.floor(box.width / h);
  return clampInt(estimated || 1, 1, 60);
}

function sanitizeBoundaries(boundaries: number[], segmentCount: number): number[] {
  const expected = Math.max(2, segmentCount) - 1;
  const raw = Array.isArray(boundaries) ? boundaries : [];
  if (raw.length !== expected) return [];

  const cleaned = raw.map((n) => clamp01(n)).sort((a, b) => a - b);
  const out: number[] = [];
  let last = 0;
  for (const v of cleaned) {
    if (v <= 0 || v >= 1) continue;
    if (v <= last) continue;
    out.push(v);
    last = v;
  }
  return out.length === expected ? out : [];
}

/** Build splitParts from legacy single-box segmentation config. */
export function deriveSplitPartsFromSegmentation(coords: unknown): SplitPart[] | null {
  const box = baseRelativeBox(coords);
  const segmentation = readSegmentation(coords);
  if (!box || !segmentation) return null;

  const count = segmentCountFromSegmentation(segmentation, box);
  if (count < 2) return null;

  const boundaries = segmentation.boundaries
    ? sanitizeBoundaries(segmentation.boundaries, count)
    : [];

  const stops: number[] = boundaries.length
    ? [0, ...boundaries, 1]
    : Array.from({ length: count + 1 }, (_, i) => i / count);

  const parts: SplitPart[] = [];
  let charStart = 0;
  for (let i = 0; i < stops.length - 1; i++) {
    const segStart = stops[i];
    const segEnd = stops[i + 1];
    const part = normalizeSplitPart({
      x: box.x + segStart * box.width,
      y: box.y,
      width: (segEnd - segStart) * box.width,
      height: box.height,
      start: charStart,
      length: 1,
    });
    if (part) parts.push(part);
    charStart += 1;
  }

  return parts.length >= 2 ? parts : null;
}

/** splitParts when present, otherwise legacy segmentation-derived boxes. */
export function resolveSplitParts(coords: unknown): SplitPart[] | null {
  return getSplitParts(coords) ?? deriveSplitPartsFromSegmentation(coords);
}

export function splitPartToPixels(
  part: SplitPart,
  canvasWidth: number,
  canvasHeight: number
): { x: number; y: number; width: number; height: number } {
  return {
    x: clamp01(part.x) * canvasWidth,
    y: clamp01(part.y) * canvasHeight,
    width: clamp01(part.width) * canvasWidth,
    height: clamp01(part.height) * canvasHeight,
  };
}

function clampPx(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

/**
 * HTML overlay font size for a full (non-split) field box in PdfFieldPlacer preview.
 * Height-first; width cap uses the whole field, not per-character slice width.
 */
export function overlayFontSizeForFieldBox(params: {
  widthPx: number;
  heightPx: number;
  text: string;
}): number {
  const { widthPx, heightPx, text } = params;
  if (!widthPx || !heightPx) return 12;

  const base = heightPx * 0.6;
  const lengthPenalty = text.length > 28 ? 0.8 : text.length > 18 ? 0.9 : 1;
  const widthCap = (widthPx / Math.max(6, Math.min(18, text.length || 1))) * 1.2;
  return clampPx(Math.min(base, widthCap) * lengthPenalty, 8, 22);
}

/**
 * Preview overlay font for one split slice — sizes from the parent field box so
 * slice text matches adjacent non-split fields (avoids per-slice width shrink).
 */
export function fontSizeForSplitOverlayPart(params: {
  parentWidthPx: number;
  parentHeightPx: number;
  fieldValueText: string;
  sliceText: string;
}): number {
  const sizingText = params.fieldValueText || params.sliceText || "0";
  return overlayFontSizeForFieldBox({
    widthPx: params.parentWidthPx,
    heightPx: params.parentHeightPx,
    text: sizingText,
  });
}

/** Same height-first fit logic as pdfFormFiller regular text fields. */
export function fontSizeForCanvasFieldBox(
  widthPx: number,
  heightPx: number,
  text: string,
  ctx: CanvasRenderingContext2D
): number {
  let size = Math.min(14, heightPx * 0.7);

  for (let attempt = 0; attempt < 5; attempt++) {
    ctx.font = `${size}px Helvetica, Arial, sans-serif`;
    const tw = ctx.measureText(text).width;
    if (tw <= widthPx - 4) break;
    size *= 0.85;
  }

  return Math.max(6, size);
}

/**
 * Per-character split boxes: height-first sizing aligned with preview overlays.
 * Avoids shrinking to illegible sizes when the box is narrow but tall.
 */
export function fontSizeForSplitCanvasBox(
  widthPx: number,
  heightPx: number,
  text: string,
  ctx: CanvasRenderingContext2D,
  referenceHeightPx?: number
): number {
  const effectiveH = Math.max(heightPx, referenceHeightPx ?? 0);
  if (!widthPx || !effectiveH) return 12;

  const base = effectiveH * 0.65;
  const charLen = Math.max(1, text.length);
  const widthCap = (widthPx / Math.max(6, Math.min(18, charLen))) * 1.15;
  let size = Math.min(base, widthCap, effectiveH * 0.85);
  const floor = clampPx(effectiveH * 0.5, 8, 22);
  const ceiling = Math.min(effectiveH * 0.85, 28);
  size = clampPx(size, floor, ceiling);

  ctx.font = `${size}px Helvetica, Arial, sans-serif`;
  let attempts = 0;
  const minAfterShrink = floor * 0.92;
  while (
    ctx.measureText(text).width > widthPx - 2 &&
    size > minAfterShrink &&
    attempts < 3
  ) {
    size *= 0.92;
    attempts++;
    ctx.font = `${size}px Helvetica, Arial, sans-serif`;
  }

  return Math.max(minAfterShrink, size);
}

export type DrawCenteredTextOptions = {
  /** Parent field height (px) when part boxes under-report height. */
  referenceHeightPx?: number;
};

/** Draw text centered in a pixel box on a canvas context. */
export function drawCenteredTextInBox(
  ctx: CanvasRenderingContext2D,
  text: string,
  box: { x: number; y: number; width: number; height: number },
  options?: DrawCenteredTextOptions
): void {
  if (!text) return;

  const size = fontSizeForSplitCanvasBox(
    box.width,
    box.height,
    text,
    ctx,
    options?.referenceHeightPx
  );
  ctx.font = `${size}px Helvetica, Arial, sans-serif`;
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#000000";
  ctx.globalAlpha = 1;

  const metrics = ctx.measureText(text);
  const x = box.x + (box.width - metrics.width) / 2;
  const y = box.y + box.height / 2;
  ctx.fillText(text, x, y);
}
